import { useEffect, useRef, useState, type PointerEvent } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, pluralize } from "@/lib/utils";

import { useOpenedBy } from "../hooks/useOpenedBy";
import { useTabDots, type TabDot } from "../hooks/useTabDots";
import { caretStyle, toCaretColour } from "../theme";
import { PhoneSheet, useIsPhone } from "./PhoneSheet";

const CLOSE_DELAY_MS = 150;
const MAX_DOTS = 3;

function Dot({ dot, className }: { dot: TabDot; className?: string }) {
  return (
    <span
      style={caretStyle(toCaretColour(dot.colour))}
      className={cn("block h-2.5 w-2.5 shrink-0 rounded-full bg-caret", className)}
    />
  );
}

function where(dot: TabDot): string {
  const source = dot.source === "application" ? "Application" : "Chat";
  if (!dot.device) return source;
  return `${dot.device === "mobile" ? "Mobile" : "Desktop"} · ${source}`;
}

/**
 * The tabs this chat is open in, as overlapping caret-colour dots by the model: only when there is
 * more than one. Hovering (mouse) or tapping opens the list of them.
 */
export function TabDots({ threadId }: { threadId: number | null }) {
  const { show, dots } = useTabDots(threadId);
  const phone = useIsPhone();
  const [open, setOpen] = useState(false);
  const closing = useRef<number>();
  // Opened from the keyboard, the list takes focus so it can be read; on hover it never does.
  const { openedBy, openedFrom, triggerProps } = useOpenedBy();
  useEffect(() => () => window.clearTimeout(closing.current), []);

  if (!show) return null;
  const hover = (next: boolean) => (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    window.clearTimeout(closing.current);
    // A hover open is never a keyboard one, whatever key the trigger saw last (an arrow opens
    // nothing here) or however a keyboard-opened list closed (hover's own close resets nothing).
    if (next) {
      openedBy.current = null;
      setOpen(true);
    } else closing.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  const list = dots.map((dot) => (
    <div
      key={dot.tabId}
      className="flex items-center gap-2 px-2 py-1.5 text-xs max-sm:py-3 max-sm:text-sm"
    >
      <Dot dot={dot} className="h-2 w-2" />
      <span>{where(dot)}</span>
      {dot.isThis && <em className="ml-auto pl-3 text-muted-foreground">this</em>}
    </div>
  ));
  const dotsButton = (
    <button
      type="button"
      className="flex h-8 items-center rounded-md px-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-10"
      {...triggerProps}
      onPointerEnter={phone ? undefined : hover(true)}
      onPointerLeave={phone ? undefined : hover(false)}
      onClick={phone ? () => setOpen(true) : undefined}
    >
      {dots.slice(0, MAX_DOTS).map((dot, index) => (
        <Dot key={dot.tabId} dot={dot} className={cn("ring-2 ring-card", index > 0 && "-ml-1")} />
      ))}
      {/* The dots are this tab and the others; the name counts the others. */}
      <span className="sr-only">{`Open in ${pluralize(dots.length - 1, "other tab")}`}</span>
    </button>
  );

  if (phone) {
    return (
      <>
        {dotsButton /* a tap opens the list as a sheet; a phone has no hover */}
        <PhoneSheet
          open={open}
          onOpenChange={setOpen}
          title="Open tabs"
          openedBy={openedBy}
          openedFrom={openedFrom}
        >
          {list}
        </PhoneSheet>
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{dotsButton}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-auto min-w-[12rem] p-1"
        onOpenAutoFocus={(event) => openedBy.current !== "keyboard" && event.preventDefault()}
        onPointerEnter={hover(true)}
        onPointerLeave={hover(false)}
      >
        {list}
      </PopoverContent>
    </Popover>
  );
}
